import {
  memo,
  useCallback,
  useEffect,
  useState,
  type KeyboardEvent,
} from "react"

import { Input, Label } from "@coval/ui"

import type { Annotation } from "@/types/api/Annotation"
import type { MetricMetadata } from "@/types/metrics/metadata"

export interface DecisionTreeResultJson {
  score: number
  intent_expressed: boolean
  tree_triggered: boolean
  tree_definition: {
    id: string
    label: string
    parent: string | null
    edge_label?: string
  }[]
  actual_path: string[]
  expected_path: string[]
  diverged_at_node: string | null
  nodes_visited: {
    node_id: string
    member_input: string
    expected_bot_response: string
    actual_bot_response: string
    correct: boolean
    message_index: number
  }[]
  critical_failures: {
    node_id: string
    failure: string
    message_index: number
  }[]
  non_critical_failures: {
    node_id: string
    failure: string
    message_index: number
  }[]
  critical_failure_count: number
  non_critical_failure_count: number
  model_version: string
}

export function isDecisionTreeResult(
  value: unknown,
): value is DecisionTreeResultJson {
  return (
    typeof value === "object" &&
    value !== null &&
    "tree_definition" in value &&
    Array.isArray((value as DecisionTreeResultJson).tree_definition)
  )
}

export function NodeDetailTable({
  nodesVisited,
  onMessageClick,
}: {
  nodesVisited: DecisionTreeResultJson["nodes_visited"]
  onMessageClick?: (messageIndex: number) => void
}) {
  if (nodesVisited.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground">Nodes Visited</h3>
      <div className="overflow-x-auto rounded-lg border border-content2">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-content1/50 border-b border-content2">
              <th className="text-left px-3 py-2 font-medium text-coval-text-muted">
                Node
              </th>
              <th className="text-left px-3 py-2 font-medium text-coval-text-muted">
                Expected
              </th>
              <th className="text-left px-3 py-2 font-medium text-coval-text-muted">
                Actual
              </th>
              <th className="text-center px-3 py-2 font-medium text-coval-text-muted">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {nodesVisited.map((node, nodeIndex) => (
              <tr
                key={`${node.node_id}-${nodeIndex}`}
                className="border-b border-content2 last:border-b-0 hover:bg-content1/30 cursor-pointer"
                onClick={() => onMessageClick?.(node.message_index)}
              >
                <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">
                  {node.node_id}
                </td>
                <td className="px-3 py-2 text-coval-text-muted max-w-[200px] truncate">
                  {node.expected_bot_response}
                </td>
                <td className="px-3 py-2 text-coval-text-muted max-w-[200px] truncate">
                  {node.actual_bot_response}
                </td>
                <td className="px-3 py-2 text-center">
                  {node.correct ? (
                    <span className="text-coval-success-text font-medium">
                      Pass
                    </span>
                  ) : (
                    <span className="text-coval-danger-text font-medium">
                      Fail
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function FailureList({
  title,
  failures,
  variant,
  onMessageClick,
}: {
  title: string
  failures: DecisionTreeResultJson["critical_failures"]
  variant: "danger" | "warning"
  onMessageClick?: (messageIndex: number) => void
}) {
  if (failures.length === 0) {
    return null
  }

  const colorClass =
    variant === "danger" ? "text-coval-danger-text" : "text-coval-warning-text"

  return (
    <div className="space-y-2">
      <h3 className={`text-sm font-semibold ${colorClass}`}>{title}</h3>
      <div className="space-y-1.5">
        {failures.map((failure, failureIndex) => (
          <div
            key={`${failure.node_id}-${failureIndex}`}
            className="text-xs bg-content1/50 rounded-lg p-3 border border-content2 cursor-pointer hover:bg-content1/70"
            onClick={() => onMessageClick?.(failure.message_index)}
          >
            <span className="font-medium text-foreground">
              {failure.node_id}:
            </span>{" "}
            <span className="text-coval-text-muted">{failure.failure}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export interface DecisionTreeReviewJson {
  visited_nodes: string[]
  score?: number | null
}

export function getReviewJson(
  annotation: Annotation | null,
): DecisionTreeReviewJson {
  const json = annotation?.ground_truth_json
  if (
    typeof json === "object" &&
    json !== null &&
    "visited_nodes" in json &&
    Array.isArray(json.visited_nodes)
  ) {
    const parsed = json as unknown as DecisionTreeReviewJson
    return {
      visited_nodes: parsed.visited_nodes,
      score: parsed.score ?? null,
    }
  }
  return { visited_nodes: [], score: null }
}

interface DecisionTreeScoreInputProps {
  currentScore: number | null
  onScoreChange: (score: number | null) => void
  inputId?: string
  label?: string
}

export const DecisionTreeScoreInput = memo(function DecisionTreeScoreInput({
  currentScore,
  onScoreChange,
  inputId = "decision-tree-score",
  label = "Score",
}: DecisionTreeScoreInputProps) {
  const [inputValue, setInputValue] = useState(
    currentScore != null ? String(currentScore) : "",
  )

  useEffect(() => {
    setInputValue(currentScore != null ? String(currentScore) : "")
  }, [currentScore])

  const saveValue = useCallback(() => {
    const trimmed = inputValue.trim()
    if (trimmed === "") {
      if (currentScore != null) {
        onScoreChange(null)
      }
      return
    }
    const numericValue = Number(trimmed)
    if (!Number.isNaN(numericValue)) {
      onScoreChange(numericValue)
    } else {
      setInputValue(currentScore != null ? String(currentScore) : "")
    }
  }, [inputValue, currentScore, onScoreChange])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault()
        event.currentTarget.blur()
        return
      }
      event.stopPropagation()
    },
    [],
  )

  return (
    <div className="flex flex-col gap-3">
      <Label htmlFor={inputId}>{label}</Label>
      <Input
        id={inputId}
        type="text"
        inputMode="numeric"
        aria-label={label}
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        onBlur={saveValue}
        onKeyDown={handleKeyDown}
        placeholder="1-5"
        className="w-12"
      />
    </div>
  )
})

export function HumanReviewInstructionsDisplay({
  instructions,
}: {
  instructions: MetricMetadata["human_review_instructions"]
}) {
  const [isExpanded, setIsExpanded] = useState(true)

  if (!instructions) {
    return null
  }

  const hasText = Boolean(instructions.text?.trim())
  const hasTable = Boolean(instructions.table && instructions.table.length > 0)

  if (!hasText && !hasTable) {
    return null
  }

  return (
    <div className="mb-3 pb-3 border-b border-divider">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 text-xs font-semibold text-coval-text-muted hover:text-foreground transition-colors mb-1"
        aria-expanded={isExpanded}
        aria-label={
          isExpanded ? "Hide review instructions" : "Show review instructions"
        }
      >
        <span
          className="inline-block transition-transform"
          style={{
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
        >
          &#9654;
        </span>
        Review Instructions
      </button>
      {isExpanded && (
        <div className="space-y-2 mt-1">
          {hasText && (
            <div className="text-xs text-coval-text-muted whitespace-pre-wrap bg-coval-info-surface rounded p-2 border border-coval-info-border">
              {instructions.text}
            </div>
          )}
          {hasTable && (
            <div className="overflow-x-auto rounded border border-content2">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-content1/50 border-b border-content2">
                    {instructions.table?.[0]?.map((header, columnIndex) => (
                      <th
                        key={columnIndex}
                        className="px-2 py-1.5 text-left font-semibold text-coval-text-muted whitespace-nowrap"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {instructions.table?.slice(1).map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-content2 last:border-b-0"
                    >
                      {row.map((cell, columnIndex) => (
                        <td
                          key={columnIndex}
                          className="px-2 py-1.5 text-coval-text-muted whitespace-pre-wrap"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
